// Sidebar redesign — current-context breadcrumb rendered at the top of
// the main content area. Default segments come from a centralised rule
// table; pages can replace the trailing segment via <BreadcrumbOverride>
// (see BreadcrumbContext.tsx).

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { useBreadcrumb } from './BreadcrumbContext';
import { APP_NAME } from '../../config/region';

interface Segment {
  label: string;
  /** Optional link target for the segment. The final segment is never linked. */
  to?: string;
}

interface BreadcrumbRule {
  test: (path: string) => boolean;
  resolve: (path: string) => Segment[];
}

function titleCase(slug: string): string {
  if (!slug) return slug;
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Order matters — the first matching rule wins, so put more specific
// patterns before broader ones (e.g. /campaigns/new before /campaigns/:id).
const RULES: BreadcrumbRule[] = [
  { test: (p) => p === '/dashboard', resolve: () => [{ label: 'Dashboard' }] },
  { test: (p) => p === '/today', resolve: () => [{ label: 'Today' }] },
  { test: (p) => p === '/calls', resolve: () => [{ label: 'Calls' }] },
  {
    test: (p) => /^\/calls\/[^/]+\/transcript$/.test(p),
    resolve: () => [
      { label: 'Calls', to: '/calls' },
      { label: 'Transcript' },
    ],
  },
  {
    test: (p) => /^\/calls\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Calls', to: '/calls' },
      { label: 'Detail' },
    ],
  },
  {
    test: (p) => /^\/runs\/[^/]+\/calls\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Calls', to: '/calls' },
      { label: 'Detail' },
    ],
  },
  {
    test: (p) => /^\/runs\/[^/]+/.test(p),
    resolve: () => [
      { label: 'Sequences', to: '/sequences' },
      { label: 'Run' },
    ],
  },
  { test: (p) => p === '/inbound-calls', resolve: () => [{ label: 'Inbound' }] },
  { test: (p) => p === '/follow-ups', resolve: () => [{ label: 'Follow-ups' }] },
  { test: (p) => p === '/contacts', resolve: () => [{ label: 'Contacts' }] },
  {
    test: (p) => /^\/contacts\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Contacts', to: '/contacts' },
      { label: 'Detail' },
    ],
  },
  { test: (p) => p === '/accounts', resolve: () => [{ label: 'Accounts' }] },
  {
    test: (p) => /^\/accounts\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Accounts', to: '/accounts' },
      { label: 'Detail' },
    ],
  },
  { test: (p) => p === '/contact-lists', resolve: () => [{ label: 'Contact lists' }] },
  {
    test: (p) => /^\/contact-lists\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Contact lists', to: '/contact-lists' },
      { label: 'Detail' },
    ],
  },
  { test: (p) => p === '/deals', resolve: () => [{ label: 'Deals' }] },
  {
    test: (p) => /^\/deals\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Deals', to: '/deals' },
      { label: 'Detail' },
    ],
  },
  { test: (p) => p === '/campaigns', resolve: () => [{ label: 'Campaigns' }] },
  {
    test: (p) => p === '/campaigns/new',
    resolve: () => [
      { label: 'Campaigns', to: '/campaigns' },
      { label: 'New' },
    ],
  },
  {
    test: (p) => /^\/campaigns\/[^/]+\/edit$/.test(p),
    resolve: () => [
      { label: 'Campaigns', to: '/campaigns' },
      { label: 'Edit' },
    ],
  },
  {
    test: (p) => /^\/campaigns\/[^/]+\/edit-wizard$/.test(p),
    resolve: () => [
      { label: 'Campaigns', to: '/campaigns' },
      { label: 'Edit' },
    ],
  },
  {
    test: (p) => /^\/campaigns\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Campaigns', to: '/campaigns' },
      { label: 'Detail' },
    ],
  },
  { test: (p) => p === '/sequences', resolve: () => [{ label: 'Sequences' }] },
  {
    test: (p) => /^\/sequences\/[^/]+\/edit$/.test(p),
    resolve: () => [
      { label: 'Sequences', to: '/sequences' },
      { label: 'Edit' },
    ],
  },
  {
    test: (p) => /^\/sequences\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Sequences', to: '/sequences' },
      { label: 'Detail' },
    ],
  },
  // LT-411: legacy /create-agent route redirects to /agents; the agent
  // detail / edit / new breadcrumbs all anchor on /agents now.
  { test: (p) => p === '/create-agent', resolve: () => [{ label: 'Agents' }] },
  { test: (p) => p === '/agents', resolve: () => [{ label: 'Agents' }] },
  { test: (p) => p === '/agents/new', resolve: () => [{ label: 'Agents', to: '/agents' }, { label: 'New' }] },
  {
    test: (p) => /^\/agents\/[^/]+\/edit$/.test(p),
    resolve: () => [
      { label: 'Agents', to: '/agents' },
      { label: 'Edit' },
    ],
  },
  {
    test: (p) => /^\/agents\/[^/]+$/.test(p),
    resolve: () => [
      { label: 'Agents', to: '/agents' },
      { label: 'Detail' },
    ],
  },
  { test: (p) => p === '/analytics', resolve: () => [{ label: 'Analytics' }] },
  { test: (p) => p === '/settings', resolve: () => [{ label: 'Settings' }] },
  {
    test: (p) => p.startsWith('/settings/'),
    resolve: (p) => {
      const seg = p.split('/').slice(2).filter(Boolean);
      return [
        { label: 'Settings', to: '/settings' },
        ...seg.map((s, i) => ({ label: titleCase(s), to: i < seg.length - 1 ? '/settings/' + seg.slice(0, i + 1).join('/') : undefined })),
      ];
    },
  },
  { test: (p) => p === '/profile', resolve: () => [{ label: 'Profile' }] },
  { test: (p) => p === '/feedback', resolve: () => [{ label: 'Feedback' }] },
  { test: (p) => p === '/support', resolve: () => [{ label: 'Support' }] },
  { test: (p) => p === '/admin', resolve: () => [{ label: 'Admin' }] },
  {
    test: (p) => p === '/admin/feedbacks',
    resolve: () => [{ label: 'Admin', to: '/admin' }, { label: 'Feedbacks' }],
  },
  {
    test: (p) => p === '/admin/tickets',
    resolve: () => [{ label: 'Admin', to: '/admin' }, { label: 'Tickets' }],
  },
  {
    test: (p) => p.startsWith('/admin/users/'),
    resolve: () => [{ label: 'Admin', to: '/admin' }, { label: 'User' }],
  },
];

function resolveSegments(pathname: string): Segment[] {
  for (const rule of RULES) {
    if (rule.test(pathname)) return rule.resolve(pathname);
  }
  // Fallback — pretty-print the trailing path segment so unmapped routes
  // still produce a sensible breadcrumb.
  const tail = pathname.split('/').filter(Boolean).pop();
  return [{ label: tail ? titleCase(tail) : 'Home' }];
}

export function getBreadcrumbSegments(pathname: string): Segment[] {
  return resolveSegments(pathname);
}

interface BreadcrumbProps {
  /**
   * Routes that should render no breadcrumb at all — e.g. the dashboard
   * (the breadcrumb would just say "Cold Caller › Dashboard" with no
   * useful context to add).
   */
  hiddenOnPaths?: string[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ hiddenOnPaths }) => {
  const location = useLocation();
  const { override } = useBreadcrumb();

  if (hiddenOnPaths && hiddenOnPaths.includes(location.pathname)) {
    return null;
  }

  const segments = resolveSegments(location.pathname);
  if (segments.length === 0) return null;

  // Apply override to the trailing segment. Detail pages set a domain
  // label (e.g. the campaign name) and we drop in that label without
  // changing the rest of the trail.
  const resolved: Segment[] = override
    ? [...segments.slice(0, -1), { label: override }]
    : segments;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 text-xs text-slate-400 px-4 lg:px-6 pt-3"
      data-testid="breadcrumb"
    >
      <Link
        to="/dashboard"
        className="text-slate-500 hover:text-slate-200 transition-colors"
      >
        {APP_NAME}
      </Link>
      {resolved.map((seg, i) => {
        const isLast = i === resolved.length - 1;
        return (
          <React.Fragment key={`${seg.label}-${i}`}>
            <ChevronRight className="h-3 w-3 text-slate-600 flex-shrink-0" aria-hidden />
            {isLast || !seg.to ? (
              <span
                className={isLast ? 'text-slate-100 font-medium truncate max-w-[40ch]' : 'text-slate-500'}
                aria-current={isLast ? 'page' : undefined}
              >
                {seg.label}
              </span>
            ) : (
              <Link
                to={seg.to}
                className="text-slate-500 hover:text-slate-200 transition-colors"
              >
                {seg.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
